import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { BannerCreateRequest, BannerUpdateRequest } from '../../../types/admin';
import { 
  getBannerList, 
  uploadBannerImage,
  createBanner, 
  updateBanner, 
  deleteBanner,
  getHomeData,
  getLectureList,
  getLectureCurationList,
  createLectureCurationSection,
  updateLectureCurationSection,
  deleteLectureCurationSection,
  addLectureToCuration,
  removeLectureFromCuration,
  LectureListItem,
  getMentorList,
  getCoffeeChatCurationList,
  createCoffeeChatCurationSection,
  updateCoffeeChatCurationSection,
  deleteCoffeeChatCurationSection,
  addMentorToCuration,
  removeMentorFromCuration,
  MentorInfo
} from '../../../services/admin/homeApi';
import { getAdminCoffeeChatUI, CoffeeChatSection } from '../../../services/admin/coffeeChatBannerApi';
import {
  getActivePopups,
  createPopupBanner,
  updatePopupBanner,
  deletePopupBanner,
  PopupBanner as PopupBannerType,
} from '../../../services/admin/popupApi';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  PageContent,
  Section,
  SectionHeader,
  SectionTitle,
  BannerContent,
  DragHandle,
  BannerImage,
  BannerForm,
  TitleRow,
  TitleInput,
  ColorInputWrapper,
  ColorPicker,
  ColorTextInput,
  CaptionRow,
  CaptionInput,
  ImageUploadWrapper,
  ImagePlaceholder,
  UploadIcon,
  UploadedImage,
  InputFieldWrapper,
  ButtonWrapper,
  CategorySection,
  TableContainer,
  Table,
  TableButton,
  TableButtonDelete,
  CurationContent,
  CurationForm,
  FormGroup,
  DescriptionInput,
  CurationButtons,
  LectureGrid,
  LectureCard,
  LectureCardImage,
  LectureCardContent,
  LectureTitle,
  LecturerName,
  RemoveLectureButton,
  AddLectureCard,
  NoImage,
  LectureModal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  LectureList,
  LectureListItem as LectureListItemRow,
  LectureListImage,
  LectureListContent,
  LectureTags,
  Tag,
  SaveSection,
  SaveActionsWrapper,
  TempSaveButton,
  SaveButton,
  SectionActions,
  LoadingMessage,
  ErrorMessage,
  AddedIndicator,
  LoadingIndicator,
  Spinner,
  NoMoreData,
  RetryButton,
  PopupPreview,
  PopupImageRow,
  PopupHint,
  PopupUploadButton,
  PopupTitleRow,
  PopupUrlInput,
  PopupDeleteButton,
  PopupSaveRow,
} from './HomeManagementStyles';

// 로컬 배너 타입 (API 타입과 호환되도록 수정)
interface LocalBanner {
  id: string | number; // 문자열(임시) 또는 숫자(서버)
  title: string;
  text: string; // API 호환
  image_url: string; // API 호환  
  font_color_hex_code: string; // API 호환
  description?: string; // UI용 추가 필드
  color?: string; // UI용 추가 필드
  imageUrl?: string; // UI용 추가 필드
  imageFile?: File; // 업로드할 파일
  isNew?: boolean; // 새로 추가된 배너인지 여부
  isDirty?: boolean; // 수정된 배너인지 여부
}

// 커피챗 큐레이션 관련 타입
interface CoffeeChatCurationSet {
  id: string | number;
  title: string;
  description: string;
  mentors: {
    id: number;
    relation_id?: number; // API 삭제를 위한 relation_id 추가
    name: string;
    department: string;
    image_url: string;
    tags: string[];
  }[];
  isNew?: boolean;
  isDirty?: boolean;
  isExpanded?: boolean; // 멘토 선택 모달 확장 여부
}
// 클래스 큐레이션 관련 타입
interface LectureCurationSet {
  id: string | number;
  title: string;
  description: string;
  lectures: {
    id: number;
    relation_id?: number; // API 삭제를 위한 relation_id 추가
    title: string;
    thumbnail_url: string;
    lecturer_name?: string;
  }[];
  isNew?: boolean;
  isDirty?: boolean;
  isExpanded?: boolean; // 강의 선택 모달 확장 여부
}

interface ImageUploadProps {
  className?: string;
  onImageChange?: (imageUrl: string | null, file?: File) => void;
  imageUrl?: string;
}

interface InputFieldProps {
  placeholder: string;
  type?: string;
  className?: string;
  value?: string;
  onChange?: (value: string) => void;
}

interface ButtonProps {
  children: React.ReactNode;
  variant?: 'outlined' | 'primary' | 'text';
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
}

// 배너 플러스 아이콘 (32x32, #448181 색상)
const BannerPlusIcon: React.FC = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <path d="M15.9994 4.13725C16.6622 4.13725 17.1994 4.67451 17.1994 5.33725V14.8039H26.6661C27.3288 14.8039 27.8661 15.3412 27.8661 16.0039C27.8661 16.6667 27.3288 17.2039 26.6661 17.2039H17.1994V26.6706C17.1994 27.3333 16.6622 27.8706 15.9994 27.8706C15.3367 27.8706 14.7994 27.3333 14.7994 26.6706V17.2039H5.33281C4.67007 17.2039 4.13281 16.6667 4.13281 16.0039C4.13281 15.3412 4.67007 14.8039 5.33281 14.8039H14.7994V5.33725C14.7994 4.67451 15.3367 4.13725 15.9994 4.13725Z" fill="#448181"/>
  </svg>
);

// 플러스 아이콘 (24x24로 스케일 조정, #448181 색상 - 제공된 SVG 사용)
const PlusIcon: React.FC = () => (
  <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15.9994 4.13721C16.6622 4.13721 17.1994 4.67447 17.1994 5.33721V14.8039H26.6661C27.3288 14.8039 27.8661 15.3411 27.8661 16.0039C27.8661 16.6666 27.3288 17.2039 26.6661 17.2039H17.1994V26.6705C17.1994 27.3333 16.6622 27.8705 15.9994 27.8705C15.3367 27.8705 14.7994 27.3333 14.7994 26.6705V17.2039H5.33281C4.67007 17.2039 4.13281 16.6666 4.13281 16.0039C4.13281 15.3411 4.67007 14.8039 5.33281 14.8039H14.7994V5.33721C14.7994 4.67447 15.3367 4.13721 15.9994 4.13721Z" fill="#448181"/>
  </svg>
);

// 마이너스 아이콘 (24x24, #448181 색상)
const MinusIcon: React.FC = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M3.09961 11.9995C3.09961 11.5025 3.50255 11.0995 3.99961 11.0995H19.9995C20.4966 11.0995 20.8995 11.5025 20.8995 11.9995C20.8995 12.4966 20.4966 12.8995 19.9995 12.8995H3.99961C3.50255 12.8995 3.09961 12.4966 3.09961 11.9995Z" fill="#448181"/>
  </svg>
);

// 드래그 핸들 아이콘 (기존 UI에 방해되지 않게 작게)
const DragHandleIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ cursor: 'grab' }}>
    <circle cx="3" cy="3" r="1" fill="#ccc"/>
    <circle cx="9" cy="3" r="1" fill="#ccc"/>
    <circle cx="3" cy="6" r="1" fill="#ccc"/>
    <circle cx="9" cy="6" r="1" fill="#ccc"/>
    <circle cx="3" cy="9" r="1" fill="#ccc"/>
    <circle cx="9" cy="9" r="1" fill="#ccc"/>
  </svg>
);

// 이미지 업로드 컴포넌트
const ImageUpload: React.FC<ImageUploadProps> = ({ className = "", onImageChange, imageUrl }) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleImageClick = () => {
    // /// console.log('Image upload clicked');
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // /// console.log('File selected:', file);
    if (file) {
      const url = URL.createObjectURL(file);
      // /// console.log('Image URL created:', url);
      onImageChange?.(url, file);
    }
  };

  // 배너용인지 카드용인지 구분하여 아이콘 선택
  const isBanner = className.includes('bannerImage');

  return (
    <ImageUploadWrapper className={className} onClick={handleImageClick}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      <ImagePlaceholder>
        {imageUrl ? (
          <UploadedImage src={imageUrl} alt="Uploaded" />
        ) : (
          <UploadIcon>
            {isBanner ? <BannerPlusIcon /> : <PlusIcon />}
          </UploadIcon>
        )}
      </ImagePlaceholder>
    </ImageUploadWrapper>
  );
};

// 입력 필드 컴포넌트
const InputField: React.FC<InputFieldProps> = ({
  placeholder,
  type = "text",
  className = "",
  value,
  onChange
}) => (
  <InputFieldWrapper className={className}>
    <input
      type={type}
      placeholder={placeholder}
      className="input"
      value={value}
      onChange={(e) => onChange?.(e.target.value)}
    />
  </InputFieldWrapper>
);

// 버튼 컴포넌트
const Button: React.FC<ButtonProps> = ({ children, variant = "outlined", onClick, className = "", disabled = false }) => (
  <ButtonWrapper
    $variant={variant}
    $disabled={disabled}
    className={className}
    onClick={onClick}
    disabled={disabled}
  >
    {children}
  </ButtonWrapper>
);

// 배너 섹션 컴포넌트 - 즉시 저장에서 일괄 저장으로 변경
const BannerSection: React.FC = () => {
  const [banners, setBanners] = useState<LocalBanner[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 캐시 무효화 쿼리 추가
  const withCacheBuster = (url?: string) => (url ? `${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}` : '');

  // 배너 목록 조회
  const loadBanners = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // /// console.log('배너 목록 조회 시작...');
      const apiBanners = await getBannerList();
      /// console.log('API에서 받은 배너 목록:', {apiBanners});
      
      // API 배너를 로컬 배너 형식으로 변환
      const localBanners: LocalBanner[] = apiBanners.map(banner => ({
        id: banner.id,
        title: banner.title,
        text: banner.text,
        image_url: banner.image_url, // 올바른 매핑
        font_color_hex_code: banner.font_color_hex_code, // 올바른 매핑
        // UI용 필드도 추가
        description: banner.text,
        color: banner.font_color_hex_code, // 올바른 매핑
        imageUrl: withCacheBuster(banner.image_url), // 캐시 무효화
        isNew: false,
        isDirty: false
      }));

      // /// console.log('변환된 로컬 배너:', localBanners);

      setBanners(localBanners.length > 0 ? localBanners : [
        createEmptyBanner()
      ]);
    } catch (err: any) {
      /// console.error('배너 목록 조회 실패:', err);
      setError(err.message || '배너 목록을 불러올 수 없습니다.');
      // 기본 배너 하나 생성
      setBanners([createEmptyBanner()]);
    } finally {
      setLoading(false);
    }
  }, []);

  // 빈 배너 생성 헬퍼 함수
  const createEmptyBanner = (): LocalBanner => ({
    id: `temp-${Date.now()}`,
    title: "",
    text: "",
    image_url: "",
    font_color_hex_code: "#000000",
    description: "",
    color: "#000000",
    imageUrl: "",
    isNew: true,
    isDirty: false
  });

  // 컴포넌트 마운트 시 배너 목록 조회
  useEffect(() => {
    loadBanners();
  }, [loadBanners]);

  // 배너 추가
  const addBanner = () => {
    if (banners.length < 4) {
      const newBanner = createEmptyBanner();
      setBanners(prev => [...prev, newBanner]);
    }
  };

  // 배너 삭제 (즉시 삭제)
  const removeBanner = async (bannerId: string | number) => {
    if (banners.length <= 1) return;

    try {
      const banner = banners.find(b => b.id === bannerId);
      if (!banner) return;

      // 서버에 저장된 배너인 경우 API 호출
      if (typeof bannerId === 'number' && !banner.isNew) {
        await deleteBanner(bannerId);
      }

      setBanners(prev => prev.filter(banner => banner.id !== bannerId));
      alert('배너가 삭제되었습니다.');
    } catch (err: any) {
      /// console.error('배너 삭제 실패:', err);
      alert(err.message || '배너 삭제에 실패했습니다.');
    }
  };

  // 배너 업데이트
  const updateBannerLocal = (bannerId: string | number, field: string, value: string | File) => {
    // /// console.log('배너 업데이트 호출:', { bannerId, field, value });
    
    setBanners(prev => prev.map(banner => {
      if (banner.id !== bannerId) return banner;

      const updates: Partial<LocalBanner> = { isDirty: true };

      if (field === 'imageFile' && value instanceof File) {
        updates.imageFile = value;
        updates.imageUrl = URL.createObjectURL(value);
        updates.image_url = URL.createObjectURL(value);
        // /// console.log('이미지 파일 업데이트:', { bannerId, file: value, url: updates.imageUrl });
      } else if (typeof value === 'string') {
        if (field === 'title') {
          updates.title = value;
        } else if (field === 'description') {
          updates.description = value;
          updates.text = value;
        } else if (field === 'color') {
          updates.color = value;
          updates.font_color_hex_code = value;
        }
      }

      const updatedBanner = { ...banner, ...updates };
      // /// console.log('배너 업데이트 완료:', updatedBanner);
      return updatedBanner;
    }));
  };

  // 파일 선택 시: 업로드만 수행해 image_url에 저장, 저장 버튼에서만 API 호출
  const handleBannerImageChange = async (bannerId: string | number, file: File) => {
    let previousBannerState: LocalBanner | null = null;
    try {
      const banner = banners.find(b => b.id === bannerId);
      if (!banner) return;

      // 이전 상태 백업 및 즉시 미리보기 표시
      previousBannerState = { ...banner };
      const previewUrl = URL.createObjectURL(file);
      setBanners(prev => prev.map(b => (
        b.id === bannerId ? { ...b, imageFile: file, imageUrl: previewUrl, isDirty: true } : b
      )));

      // 업로드 수행
      setLoading(true);
      const uploadedImageUrl = await uploadBannerImage(file);

      // 신규 배너: 업로드 URL만 저장하고 종료 (생성은 저장 시)
      if (banner.isNew || typeof bannerId !== 'number') {
        setBanners(prev => prev.map(b => (
          b.id === bannerId
            ? { ...b, imageFile: undefined, image_url: uploadedImageUrl, imageUrl: withCacheBuster(uploadedImageUrl), isDirty: true }
            : b
        )));
        return;
      }

      // 기존 배너: 업로드 직후 즉시 PUT으로 반영
      const bannerRequest: BannerUpdateRequest = {
        title: banner.title,
        description: banner.description || banner.text,
        fontColorHexCode: banner.color || banner.font_color_hex_code,
        bannerImageUrl: uploadedImageUrl
      };
      await updateBanner(bannerId as number, bannerRequest);

      // 로컬 상태 갱신 및 더티 해제
      setBanners(prev => prev.map(b => (
        b.id === bannerId
          ? { ...b, imageFile: undefined, image_url: uploadedImageUrl, imageUrl: withCacheBuster(uploadedImageUrl), isDirty: false }
          : b
      )));

      // 선택: 서버 기준 목록 동기화
      await loadBanners();
    } catch (err) {
      /// console.error('배너 이미지 업로드/업데이트 실패:', err);
      alert((err as any)?.message || '배너 이미지를 저장하는 중 오류가 발생했습니다.');
      // 롤백
      if (previousBannerState) {
        setBanners(prev => prev.map(b => (b.id === bannerId ? previousBannerState as LocalBanner : b)));
      }
    } finally {
      setLoading(false);
    }
  };

  // 모든 배너 저장 (일괄 저장)
  const saveAllBanners = async () => {
    try {
      setLoading(true);
      setError(null);

      // 저장할 내용이 있는지 먼저 확인
      const hasNewBanners = banners.some(banner => banner.isNew && (banner.title || banner.description || banner.image_url || banner.imageFile));
      const hasUpdatedBanners = banners.some(banner => !banner.isNew && banner.isDirty);

      if (!hasNewBanners && !hasUpdatedBanners) {
        alert('저장할 변경사항이 없습니다.');
        return;
      }

      let savedCount = 0;

      for (const banner of banners) {
        // console.log('[배너 저장][검사]', {
        //   id: banner.id,
        //   isNew: banner.isNew,
        //   isDirty: banner.isDirty,
        //   title: banner.title,
        //   hasDesc: !!(banner.description || banner.text),
        //   image_url: banner.image_url,
        //   imageUrlPreview: banner.imageUrl
        // });
        // 새 배너이면서 내용이 있는 경우 생성 (이미지 URL은 파일 선택 시 확보)
        if (banner.isNew && (banner.title || banner.description || banner.image_url || banner.imageFile)) {
          const imageUrl = banner.image_url || '';
          if (!imageUrl) {
            throw new Error(`배너 "${banner.title || '제목 없음'}"의 이미지 URL이 없습니다. 이미지를 선택하여 업로드를 먼저 진행해주세요.`);
          }

          const bannerRequest: BannerCreateRequest = {
            title: banner.title,
            description: banner.description || banner.text,
            fontColorHexCode: banner.color || banner.font_color_hex_code,
            bannerImageUrl: imageUrl
          };

          /// console.log('[배너 저장][생성시도]', { id: banner.id, bannerRequest });
          await createBanner(bannerRequest);
          /// console.log('[배너 저장][생성완료]', { id: banner.id });
          savedCount++;
        }
        // 기존 배너이면서 수정된 경우 업데이트 (업로드는 파일 선택 시 완료됨)
        else if (!banner.isNew && banner.isDirty && typeof banner.id === 'number') {
          // console.log('[배너 저장][업데이트시도]', {
          //   id: banner.id,
          //   title: banner.title,
          //   description: banner.description,
          //   color: banner.color,
          //   image_url: banner.image_url
          // });

          const imageUrl = banner.image_url || '';

          if (!imageUrl) {
            throw new Error(`배너 "${banner.title || '제목 없음'}"의 이미지 URL을 찾을 수 없습니다.\n이미지 업로드 후 다시 저장해주세요.`);
          }

          const bannerRequest: BannerUpdateRequest = {
            title: banner.title,
            description: banner.description || banner.text,
            fontColorHexCode: banner.color || banner.font_color_hex_code,
            bannerImageUrl: imageUrl
          };

          /// console.log('배너 업데이트 API 요청 데이터:', {bannerRequest});
          const result = await updateBanner(banner.id as number, bannerRequest);
          /// console.log('[배너 저장][업데이트완료]', { id: banner.id, result });
          savedCount++;
        } else {
          // console.log('[배너 저장][스킵]', {
          //   id: banner.id,
          //   reason: banner.isNew ? '신규이나 필수값 부족(제목/설명/이미지)' : (!banner.isDirty ? '변경없음' : 'ID 형식 오류')
          // });
        }
      }

      // 저장 후 목록 새로고침
      await loadBanners();
      
      if (savedCount > 0) {
        alert(`${savedCount}개의 배너가 성공적으로 저장되었습니다.`);
      }
    } catch (err: any) {
      /// console.error('배너 저장 실패:', err);
      setError(err.message || '배너 저장에 실패했습니다.');
      alert(err.message || '배너 저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(banners);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setBanners(items);
  };

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>배너</SectionTitle>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </SectionHeader>

      {loading && <LoadingMessage>로딩 중...</LoadingMessage>}

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="banners">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {banners.map((banner, index) => (
                <Draggable key={banner.id} draggableId={String(banner.id)} index={index}>
                  {(provided, snapshot) => (
                    <BannerContent
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={{
                        ...provided.draggableProps.style,
                        border: snapshot.isDragging ? '2px dashed #448181' : 'none',
                        borderRadius: snapshot.isDragging ? '8px' : '0',
                        backgroundColor: snapshot.isDragging ? '#f8f9fa' : 'transparent',
                        paddingBottom: '4px',
                        marginBottom: '20px'
                      }}
                    >
                      {/* 작은 드래그 핸들 추가 */}
                      <DragHandle
                        {...provided.dragHandleProps}
                        title={`배너 ${index + 1} 드래그`}
                      >
                        <DragHandleIcon />
                      </DragHandle>

                      <ImageUpload
                        className="bannerImage"
                        imageUrl={banner.imageUrl}
                        onImageChange={(url, file) => {
                          if (file) {
                            handleBannerImageChange(banner.id, file);
                          }
                        }}
                      />
                      <BannerForm>
                        <TitleRow>
                          <InputField
                            placeholder={`배너 ${index + 1} 제목을 입력해주세요.`}
                            className="titleInput"
                            value={banner.title}
                            onChange={(value) => updateBannerLocal(banner.id, 'title', value)}
                          />
                          <ColorInputWrapper>
                            <ColorPicker
                              type="color"
                              value={banner.color || banner.font_color_hex_code}
                              onChange={(e) => updateBannerLocal(banner.id, 'color', e.target.value)}
                            />
                            <ColorTextInput
                              type="text"
                              value={banner.color || banner.font_color_hex_code}
                              onChange={(e) => {
                                const colorValue = e.target.value;
                                if (colorValue.length > 0 && !colorValue.startsWith('#')) {
                                  updateBannerLocal(banner.id, 'color', '#' + colorValue);
                                } else {
                                  if (colorValue.length <= 7) {
                                    updateBannerLocal(banner.id, 'color', colorValue);
                                  }
                                }
                              }}
                              placeholder="#000000"
                            />
                          </ColorInputWrapper>
                        </TitleRow>
                        <CaptionRow>
                          <InputField
                            placeholder={`배너 ${index + 1} 설명을 입력해주세요.`}
                            className="captionInput"
                            value={banner.description || banner.text}
                            onChange={(value) => updateBannerLocal(banner.id, 'description', value)}
                          />
                          <Button
                            variant="outlined"
                            onClick={addBanner}
                            disabled={banners.length >= 4}
                          >
                            <PlusIcon />
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={() => removeBanner(banner.id)}
                            disabled={banners.length <= 1}
                          >
                            <MinusIcon />
                          </Button>
                        </CaptionRow>
                      </BannerForm>
                    </BannerContent>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* 배너 저장 버튼 */}
      <SectionActions>
        <Button
          variant="primary"
          onClick={saveAllBanners}
          disabled={loading}
        >
          {loading ? '저장 중...' : '배너 저장'}
        </Button>
      </SectionActions>
    </Section>
  );
};

// 클래스 큐레이션 관리 컴포넌트 - API 적용
const ClassCurationManagement: React.FC = () => {
  const [curationSets, setCurationSets] = useState<LectureCurationSet[]>([]);
  const [availableLectures, setAvailableLectures] = useState<LectureListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lectureModalOpen, setLectureModalOpen] = useState<string | null>(null);
  
  // 무한스크롤 관련 상태
  const [lecturePage, setLecturePage] = useState(0);
  const [lectureLoading, setLectureLoading] = useState(false);
  const [lectureHasMore, setLectureHasMore] = useState(true);
  const [lectureError, setLectureError] = useState<string | null>(null);

  // 강의 목록 조회 (무한스크롤 지원)
  const loadLectures = async (page = 0, reset = false) => {
    try {
      setLectureLoading(true);
      setLectureError(null);
      
      const lectureResponse = await getLectureList(page, 20); // 페이지당 20개씩 로드
      
      if (reset) {
        setAvailableLectures(lectureResponse.content);
      } else {
        setAvailableLectures(prev => [...prev, ...lectureResponse.content]);
      }
      
      setLectureHasMore(!lectureResponse.last);
      setLecturePage(page);
    } catch (err: any) {
      /// console.error('강의 목록 조회 실패:', err);
      setLectureError(err.message || '강의 목록을 불러올 수 없습니다.');
    } finally {
      setLectureLoading(false);
    }
  };

  // 더 많은 강의 로드
  const loadMoreLectures = () => {
    if (!lectureLoading && lectureHasMore) {
      loadLectures(lecturePage + 1, false);
    }
  };

  // 강의 큐레이션 목록 조회
  const loadCurations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const curations = await getLectureCurationList();
      
      // API 데이터를 로컬 형식으로 변환
      const localCurations: LectureCurationSet[] = curations.map(curation => ({
        id: curation.home_lecture_section_id || 0, // 호환성을 위해 둘 다 체크
        title: curation.title,
        description: curation.text,
        lectures: (curation.related_lectures || []).map(lecture => ({
          id: lecture.lecture_id,
          relation_id: lecture.relation_id, // relation_id 포함
          title: lecture.title,
          thumbnail_url: lecture.thumbnail_url,
          lecturer_name: lecture.lecturer_info.name || '강연자명 없음',
          lecturer_department: lecture.lecturer_info.department || '소개 없음',
          lecturer_id: lecture.lecturer_info.id || '강연자ID 없음'
        })),
        isNew: false,
        isDirty: false
      }));

      setCurationSets(localCurations.length > 0 ? localCurations : [createEmptyCuration()]);
    } catch (err: any) {
      /// console.error('강의 큐레이션 목록 조회 실패:', err);
      setError(err.message || '강의 큐레이션 목록을 불러올 수 없습니다.');
      setCurationSets([createEmptyCuration()]);
    } finally {
      setLoading(false);
    }
  }, []);

  // 빈 큐레이션 생성
  const createEmptyCuration = (): LectureCurationSet => ({
    id: `temp-${Date.now()}`,
    title: '',
    description: '',
    lectures: [],
    isNew: true,
    isDirty: false
  });

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    loadLectures(0, true); // 초기 로드
    loadCurations();
  }, [loadCurations]);

  // 큐레이션 세트 추가
  const addCurationSet = () => {
    const newSet = createEmptyCuration();
    setCurationSets(prev => [...prev, newSet]);
  };

  // 큐레이션 세트 제거
  const removeCurationSet = async (setId: string | number) => {
    if (curationSets.length <= 1) return;

    try {
      const curation = curationSets.find(c => c.id === setId);
      if (!curation) return;

      // 서버에 저장된 큐레이션인 경우 API 호출
      if (typeof setId === 'number' && !curation.isNew) {
        // 연결된 강의 먼저 해제 후 삭제 (연결 있으면 500 발생)
        for (const lecture of curation.lectures) {
          await removeLectureFromCuration(setId, lecture.id);
        }
        await deleteLectureCurationSection(setId);
      }

      setCurationSets(prev => prev.filter(set => set.id !== setId));
      alert('큐레이션이 삭제되었습니다.');
    } catch (err: any) {
      /// console.error('큐레이션 섹션 삭제 실패:', err);
      alert(err.message || '큐레이션 섹션 삭제에 실패했습니다.');
    }
  };

  // 큐레이션 세트 업데이트
  const updateCurationSet = (setId: string | number, field: 'title' | 'description', value: string) => {
    setCurationSets(prev => prev.map(set => {
      if (set.id !== setId) return set;
      
      // 값이 실제로 변경되었는지 확인
      const currentValue = field === 'title' ? set.title : set.description;
      if (currentValue === value) return set; // 변경사항이 없으면 그대로 반환
      
      return { 
        ...set, 
        [field]: value, 
        isDirty: true // 변경사항이 있으면 isDirty를 true로 설정
      };
    }));
  };

  // 강의를 큐레이션에 추가
  const addLectureToCurationSet = async (setId: string | number, lecture: LectureListItem) => {
    try {
      const curation = curationSets.find(c => c.id === setId);
      if (!curation) {
        /// console.error('큐레이션을 찾을 수 없음:', setId);
        return;
      }

      // console.log('강의 추가 시도:', { 
      //   setId, 
      //   lectureId: lecture.lecture_info.id, 
      //   isNew: curation.isNew,
      //   curationTitle: curation.title,
      //   existingLectures: curation.lectures.map(l => l.id)
      // });

      // 이미 추가된 강의인지 확인
      if (curation.lectures.some(l => l.id === lecture.lecture_info.id)) {
        alert('이미 추가된 강의입니다.');
        return;
      }

      // 새로운 큐레이션인 경우 먼저 큐레이션을 저장하라고 안내
      if (curation.isNew) {
        // /// console.log('새로운 큐레이션이므로 저장 필요');
        alert('강의를 추가하기 전에 먼저 큐레이션 섹션을 저장해주세요.\n\n1. 제목과 설명을 입력하세요\n2. "큐레이션 저장" 버튼을 클릭하세요\n3. 저장 완료 후 강의를 추가할 수 있습니다');
        return;
      }

      // 서버에 저장된 큐레이션인 경우 즉시 강의 추가
      if (typeof setId === 'number') {
        /// console.log('기존 큐레이션에 강의 추가 데이터 (API 호출 해야함):', { setId, lectureId: lecture.lecture_info.id });
        
        try {
          const apiResponse = await addLectureToCuration({
            lectureId: lecture.lecture_info.id,
            sectionId: setId
          });

          /// console.log('강의 추가 API 응답:', {apiResponse});

          // API 호출 성공 후 로컬 상태 업데이트
          setCurationSets(prev => prev.map(set => {
            if (set.id !== setId) return set;

            const newLecture = {
              id: lecture.lecture_info.id,
              relation_id: apiResponse.relation_id, // API 응답에서 받은 relation_id 사용
              title: lecture.lecture_info.title,
              thumbnail_url: lecture.lecture_info.thumbnail_url,
              lecturer_name: lecture.lecturer_info.name
            };

            return {
              ...set,
              lectures: [...set.lectures, newLecture],
              isDirty: false // API 호출했으므로 더티 상태 해제
            };
          }));

          setLectureModalOpen(null);
          alert('강의가 성공적으로 추가되었습니다.');
          
        } catch (apiError: any) {
          /// console.error('API 에러 상세:', apiError);
          
          // 409 에러 (이미 존재하는 관계)인 경우
          if (apiError.response?.status === 409 || apiError.status === 409 || apiError.message?.includes('이미 존재') || apiError.message?.includes('already exists') || apiError.message?.includes('duplicate')) {
            alert('이 강의는 이미 해당 큐레이션에 등록되어 있습니다.\n\n페이지를 새로고침하여 최신 상태를 확인해주세요.');
            // 큐레이션 목록 새로고침
            await loadCurations();
            setLectureModalOpen(null);
          } else if (apiError.response?.status === 400 || apiError.status === 400) {
            alert('잘못된 요청입니다. 강의 정보나 큐레이션 정보를 다시 확인해주세요.');
          } else if (apiError.response?.status === 404 || apiError.status === 404) {
            alert('강의를 찾을 수 없습니다. 강의가 삭제되었거나 존재하지 않을 수 있습니다.');
          } else {
            throw apiError; // 다른 에러는 그대로 throw
          }
        }
      }
    } catch (err: any) {
      /// console.error('강의 추가 실패:', err);
      alert(err.message || '강의 추가에 실패했습니다.');
    }
  };

  // 강의를 큐레이션에서 제거
  const removeLectureFromCurationSet = async (setId: string | number, lectureId: number) => {
    try {
      const curation = curationSets.find(c => c.id === setId);
      if (!curation) return;

      // 새로운 큐레이션이거나 서버에 저장되지 않은 상태인 경우 로컬에서만 제거
      if (curation.isNew) {
        /// console.log('새로운 큐레이션에서 강의 제거 (로컬에서만 제거):', { setId, lectureId });
        
        // 로컬 상태만 업데이트 (실제 강의 ID로 찾기)
        setCurationSets(prev => prev.map(set => 
          set.id === setId 
            ? { ...set, lectures: set.lectures.filter(l => l.id !== lectureId), isDirty: true }
            : set
        ));
        return;
      }

      // 서버에 저장된 큐레이션인 경우 API 호출
      if (typeof setId === 'number') {
        /// console.log('기존 큐레이션에서 강의 제거 (API 호출):', { setId, lectureId });

        // 실제 강의 ID로 API 호출
        await removeLectureFromCuration(setId, lectureId);

        // API 호출 성공 후 로컬 상태 업데이트 (실제 강의 ID로 찾기)
        setCurationSets(prev => prev.map(set => 
          set.id === setId 
            ? { ...set, lectures: set.lectures.filter(l => l.id !== lectureId), isDirty: false }
            : set
        ));

        alert('강의가 성공적으로 제거되었습니다.');
      }
    } catch (err: any) {
      /// console.error('강의 제거 실패:', err);
      alert(err.message || '강의 제거에 실패했습니다.');
    }
  };

  // 개별 큐레이션 저장
  const saveSingleCuration = async (setId: string | number) => {
    try {
      const curation = curationSets.find(c => c.id === setId);
      if (!curation) return;

      // /// console.log('큐레이션 저장 시작:', { setId, curation });

      if (!curation.title.trim()) {
        alert('큐레이션 제목을 입력해주세요.');
        return;
      }

      if (!curation.description.trim()) {
        alert('큐레이션 설명을 입력해주세요.');
        return;
      }

      setLoading(true);

      if (curation.isNew) {
        // /// console.log('새로운 큐레이션 생성 중...');
        // 새로운 큐레이션 생성
        const sectionData = {
          title: curation.title,
          text: curation.description
        };

        const createdSection = await createLectureCurationSection(sectionData);
        /// console.log('큐레이션 생성 완료:', {createdSection});
        
        // 로컬 상태 업데이트
        setCurationSets(prev => prev.map(set => 
          set.id === setId 
            ? { 
                ...set, 
                id: createdSection.id, 
                isNew: false, 
                isDirty: false 
              }
            : set
        ));

        alert('큐레이션이 성공적으로 저장되었습니다.\n이제 강의를 추가할 수 있습니다.');
      } else if (typeof setId === 'number' && curation.isDirty) {
        // /// console.log('기존 큐레이션 업데이트 중...');
        // 기존 큐레이션 업데이트
        const sectionData = {
          title: curation.title,
          text: curation.description
        };

        await updateLectureCurationSection(setId, sectionData);
        // /// console.log('큐레이션 업데이트 완료');
        
        // 로컬 상태 업데이트
        setCurationSets(prev => prev.map(set => 
          set.id === setId 
            ? { ...set, isDirty: false }
            : set
        ));

        alert('큐레이션이 성공적으로 업데이트되었습니다.');
      } else {
        alert('저장할 변경사항이 없습니다.');
      }
    } catch (err: any) {
      /// console.error('큐레이션 저장 실패:', err);
      alert(err.message || '큐레이션 저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 모든 큐레이션 저장
  const saveAllCurations = async () => {
    try {
      setLoading(true);
      setError(null);

      let savedCount = 0;

      for (const curation of curationSets) {
        // 새 큐레이션이면서 내용이 있는 경우에만 생성 (강의가 없는 빈 큐레이션만)
        if (curation.isNew && (curation.title || curation.description) && curation.lectures.length === 0) {
          const sectionData = {
            title: curation.title || '새 큐레이션',
            text: curation.description || '새 큐레이션 설명'
          };

          await createLectureCurationSection(sectionData);
          savedCount++;
        }
        // 기존 큐레이션이면서 수정된 경우 업데이트
        else if (!curation.isNew && curation.isDirty && typeof curation.id === 'number') {
          const sectionData = {
            title: curation.title,
            text: curation.description
          };

          await updateLectureCurationSection(curation.id, sectionData);
          savedCount++;
        }
      }

      // 저장 후 목록 새로고침
      await loadCurations();
      
      if (savedCount > 0) {
        alert(`${savedCount}개의 큐레이션이 성공적으로 저장되었습니다.`);
      } else {
        alert('저장할 변경사항이 없습니다.\n\n참고: 강의가 추가된 새 큐레이션은 이미 자동으로 저장되었습니다.');
      }
    } catch (err: any) {
      /// console.error('큐레이션 저장 실패:', err);
      setError(err.message || '큐레이션 저장에 실패했습니다.');
      alert(err.message || '큐레이션 저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>클래스 큐레이션</SectionTitle>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </SectionHeader>

      {loading && <LoadingMessage>로딩 중...</LoadingMessage>}

      {curationSets.map((curationSet, setIndex) => (
        <CurationContent key={curationSet.id} style={{ marginBottom: '32px' }}>
          <CurationForm>
            <FormGroup>
              <SectionTitle as="label" style={{ fontSize: '12px', color: 'rgba(46, 47, 51, 0.6)' }}>
                클래스 큐레이션 {setIndex + 1}
              </SectionTitle>
              <InputField
                placeholder="제목을 입력해주세요."
                value={curationSet.title}
                onChange={(value) => updateCurationSet(curationSet.id, 'title', value)}
              />
            </FormGroup>
            <InputField
              placeholder="설명을 입력해주세요."
              className="descriptionInput"
              value={curationSet.description}
              onChange={(value) => updateCurationSet(curationSet.id, 'description', value)}
            />
            <CurationButtons>
              {/* 개별 큐레이션 저장 버튼 */}
              <Button
                variant={curationSet.isNew || curationSet.isDirty ? "primary" : "outlined"}
                onClick={() => saveSingleCuration(curationSet.id)}
                disabled={loading}
              >
                {loading ? '저장 중...' :
                 curationSet.isNew ? '큐레이션 저장' :
                 curationSet.isDirty ? '변경사항 저장' : '저장됨'}
              </Button>
              <Button
                variant="outlined"
                onClick={addCurationSet}
              >
                <PlusIcon />
              </Button>
              <Button
                variant="outlined"
                onClick={() => removeCurationSet(curationSet.id)}
                disabled={curationSets.length <= 1}
              >
                <MinusIcon />
              </Button>
            </CurationButtons>
          </CurationForm>

          {/* 강의 목록 표시 */}
          <LectureGrid>
            {curationSet.lectures.map((lecture) => (
              <LectureCard key={lecture.id}>
                <LectureCardImage>
                  {lecture.thumbnail_url ? (
                    <img src={lecture.thumbnail_url} alt={lecture.title} />
                  ) : (
                    <NoImage>이미지 없음</NoImage>
                  )}
                </LectureCardImage>
                <LectureCardContent>
                  <LectureTitle>{lecture.title}</LectureTitle>
                  {lecture.lecturer_name && (
                    <LecturerName>{lecture.lecturer_name}</LecturerName>
                  )}
                </LectureCardContent>
                <RemoveLectureButton
                  onClick={() => removeLectureFromCurationSet(curationSet.id, lecture.id)}
                >
                  <MinusIcon />
                </RemoveLectureButton>
              </LectureCard>
            ))}

            {/* 강의 추가 버튼 */}
            <AddLectureCard
              $disabled={curationSet.isNew}
              onClick={() => {
                if (curationSet.isNew) {
                  alert('강의를 추가하기 전에 먼저 큐레이션을 저장해주세요.');
                  return;
                }
                // 모달 열 때 강의 목록 새로고침
                loadLectures(0, true);
                setLectureModalOpen(String(curationSet.id));
              }}
              title={curationSet.isNew ? '큐레이션을 먼저 저장해주세요' : '강의 추가'}
            >
              <PlusIcon />
              <span>강의 추가</span>
              {curationSet.isNew && (
                <small style={{ fontSize: '10px', color: '#999', marginTop: '4px' }}>
                  (큐레이션 저장 후 가능)
                </small>
              )}
            </AddLectureCard>
          </LectureGrid>

          {/* 강의 선택 모달 */}
          {lectureModalOpen === String(curationSet.id) && (
            <LectureModal>
              <ModalOverlay onClick={() => setLectureModalOpen(null)} />
              <ModalContent>
                <ModalHeader>
                  <h3>강의 선택</h3>
                  <button onClick={() => setLectureModalOpen(null)}>×</button>
                </ModalHeader>
                <ModalBody>
                  <LectureList
                    onScroll={(e) => {
                      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
                      // 스크롤이 하단 근처에 도달했을 때 더 많은 데이터 로드
                      if (scrollHeight - scrollTop <= clientHeight + 100 && !lectureLoading && lectureHasMore) {
                        loadMoreLectures();
                      }
                    }}
                  >
                    {availableLectures.map((lecture) => {
                      const isAlreadyAdded = curationSet.lectures.some(l => l.id === lecture.lecture_info.id);

                      return (
                        <LectureListItemRow
                          key={lecture.lecture_info.id}
                          $alreadyAdded={isAlreadyAdded}
                          onClick={() => {
                            if (isAlreadyAdded) {
                              alert('이 강의는 이미 추가되어 있습니다.');
                              return;
                            }
                            addLectureToCurationSet(curationSet.id, lecture);
                          }}
                          title={isAlreadyAdded ? '이미 추가된 강의' : '클릭하여 추가'}
                        >
                          <LectureListImage>
                            {lecture.lecture_info.thumbnail_url ? (
                              <img src={lecture.lecture_info.thumbnail_url} alt={lecture.lecture_info.title} />
                            ) : (
                              <NoImage>이미지 없음</NoImage>
                            )}
                          </LectureListImage>
                          <LectureListContent>
                            <h4>{lecture.lecture_info.title}</h4>
                            <p>{lecture.lecturer_info.name + "·" + lecture.lecturer_info.department}</p>
                            <LectureTags>
                              {lecture.lecture_info.tags.map((tag, idx) => (
                                <Tag key={idx}>{tag}</Tag>
                              ))}
                            </LectureTags>
                          </LectureListContent>
                          {isAlreadyAdded && (
                            <AddedIndicator>
                              ✓ 추가됨
                            </AddedIndicator>
                          )}
                        </LectureListItemRow>
                      );
                    })}

                    {/* 로딩 인디케이터 */}
                    {lectureLoading && (
                      <LoadingIndicator>
                        <Spinner />
                        <span>강의를 불러오는 중...</span>
                      </LoadingIndicator>
                    )}

                    {/* 더 이상 로드할 데이터가 없을 때 */}
                    {!lectureHasMore && availableLectures.length > 0 && (
                      <NoMoreData>
                        모든 강의를 불러왔습니다.
                      </NoMoreData>
                    )}

                    {/* 에러 메시지 */}
                    {lectureError && (
                      <ErrorMessage>
                        {lectureError}
                        <RetryButton onClick={() => loadLectures(lecturePage + 1, false)}>
                          다시 시도
                        </RetryButton>
                      </ErrorMessage>
                    )}
                  </LectureList>
                </ModalBody>
              </ModalContent>
            </LectureModal>
          )}
        </CurationContent>
      ))}

      {/* 큐레이션 저장 버튼 */}
      <SectionActions>
        <Button
          variant="primary"
          onClick={saveAllCurations}
          disabled={loading}
        >
          {loading ? '저장 중...' : '클래스 큐레이션 저장'}
        </Button>
      </SectionActions>
    </Section>
  );
};

// 카테고리 테이블 섹션 (필요시 사용)
const CategorySectionComponent: React.FC = () => (
  <CategorySection style={{ display: 'none' }}>
    <SectionHeader>
      <SectionTitle>카테고리</SectionTitle>
    </SectionHeader>
    <TableContainer>
      <Table>
        <thead>
          <tr>
            <th>연번</th>
            <th>카테고리명</th>
            <th>상위 카테고리</th>
            <th>강의 수</th>
            <th>관리</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>1</td>
            <td>인공지능 기초</td>
            <td>-</td>
            <td>6</td>
            <td>
              <TableButton onClick={() => console.log('Edit clicked')}>수정</TableButton>
              <TableButtonDelete onClick={() => console.log('Delete clicked')}>삭제</TableButtonDelete>
            </td>
          </tr>
          <tr>
            <td>2</td>
            <td>데이터 분석</td>
            <td>-</td>
            <td>6</td>
            <td>
              <TableButton onClick={() => console.log('Edit clicked')}>수정</TableButton>
              <TableButtonDelete onClick={() => console.log('Delete clicked')}>삭제</TableButtonDelete>
            </td>
          </tr>
        </tbody>
      </Table>
    </TableContainer>
  </CategorySection>
);

// 커피챗 큐레이션 관리 컴포넌트 - API 적용
const CoffeeChatCurationManagement: React.FC<{ onRefreshSections: () => Promise<any> }> = ({ onRefreshSections }) => {
  const [curationSets, setCurationSets] = useState<CoffeeChatCurationSet[]>([]);
  const [availableMentors, setAvailableMentors] = useState<MentorInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mentorModalOpen, setMentorModalOpen] = useState<string | null>(null);
  const [sections, setSections] = useState<CoffeeChatSection[]>([]);

  // 멘토 목록 조회
  const loadMentors = async () => {
    try {
      const mentorResponse = await getMentorList(0, 100, false); // 승인된 멘토만 조회
      setAvailableMentors(mentorResponse.content);
    } catch (err: any) {
      /// console.error('멘토 목록 조회 실패:', err);
    }
  };

  // 커피챗 큐레이션 목록 조회
  const loadCurations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // getHomeData API에서 coffee_chat_sections 가져오기
      const homeData = await getHomeData();
      
      // API 데이터를 로컬 형식으로 변환
      const localCurations: CoffeeChatCurationSet[] = (homeData.coffee_chat_sections || []).map(curation => ({
        id: curation.id ?? curation.coffee_chat_section_id ?? `temp-${Math.random()}`,
        title: curation.title,
        description: curation.text,
        mentors: (curation.related_persons || []).map(person => ({
          id: person.person_id,
          relation_id: person.section_related_person_id, // relation_id 포함
          name: person.name,
          department: person.department,
          image_url: person.image_url,
          tags: typeof person.tags === 'string' ? (person.tags as string).split(',').map((tag: string) => tag.trim()) : (person.tags as string[]) || []
        })),
        isNew: false,
        isDirty: false
      }));

      setCurationSets(localCurations.length > 0 ? localCurations : [createEmptyCoffeeChatCuration()]);
    } catch (err: any) {
      /// console.error('커피챗 큐레이션 목록 조회 실패:', err);
      setError(err.message || '커피챗 큐레이션 목록을 불러올 수 없습니다.');
      setCurationSets([createEmptyCoffeeChatCuration()]);
    } finally {
      setLoading(false);
    }
  }, []);

  // 빈 큐레이션 생성
  const createEmptyCoffeeChatCuration = (): CoffeeChatCurationSet => ({
    id: `temp-${Date.now()}`,
    title: '',
    description: '',
    mentors: [],
    isNew: true,
    isDirty: false
  });

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    loadMentors();
    loadCurations();
    // 초기 섹션 정보 로드
    onRefreshSections().then(result => {
      if (result && result.sections) {
        setSections(result.sections);
      }
    }).catch(err => {
      /// console.error('초기 섹션 정보 로드 실패:', err);
    });
  }, [loadCurations, onRefreshSections]);

  // 큐레이션 세트 추가
  const addCurationSet = () => {
    const newSet = createEmptyCoffeeChatCuration();
    setCurationSets(prev => [...prev, newSet]);
  };

  // 큐레이션 세트 제거
  const removeCurationSet = async (setId: string | number) => {
    if (curationSets.length <= 1) return;

    try {
      const curation = curationSets.find(c => c.id === setId);
      if (!curation) return;

      // 서버에 저장된 큐레이션인 경우 API 호출
      if (typeof setId === 'number' && !curation.isNew) {
        await deleteCoffeeChatCurationSection(setId);
      }

      setCurationSets(prev => prev.filter(set => set.id !== setId));
      alert('커피챗 큐레이션이 삭제되었습니다.');

      // 섹션 정보 새로고침
      const refreshResult = await onRefreshSections();
      if (refreshResult && refreshResult.sections) {
        setSections(refreshResult.sections);
      }
    } catch (err: any) {
      /// console.error('커피챗 큐레이션 섹션 삭제 실패:', err);
      alert(err.message || '커피챗 큐레이션 섹션 삭제에 실패했습니다.');
    }
  };

  // 큐레이션 세트 업데이트
  const updateCurationSet = (setId: string | number, field: 'title' | 'description', value: string) => {
    setCurationSets(prev => prev.map(set => {
      if (set.id !== setId) return set;
      
      // 값이 실제로 변경되었는지 확인
      const currentValue = field === 'title' ? set.title : set.description;
      if (currentValue === value) return set; // 변경사항이 없으면 그대로 반환
      
      return { 
        ...set, 
        [field]: value, 
        isDirty: true // 변경사항이 있으면 isDirty를 true로 설정
      };
    }));
  };

  // 멘토를 큐레이션에 추가
  const addMentorToCurationSet = async (setId: string | number, mentor: MentorInfo) => {
    try {
      const curation = curationSets.find(c => c.id === setId);
      if (!curation) {
        /// console.error('큐레이션을 찾을 수 없음:', setId);
        return;
      }

      // console.log('멘토 추가 시도:', { 
      //   setId, 
      //   mentorId: mentor.id, 
      //   isNew: curation.isNew,
      //   curationTitle: curation.title,
      //   existingMentors: curation.mentors.map(m => m.id)
      // });

      // 이미 추가된 멘토인지 확인
      if (curation.mentors.some(m => m.id === mentor.id)) {
        alert('이미 추가된 멘토입니다.');
        return;
      }

      // 새로운 큐레이션인 경우 먼저 큐레이션을 저장하라고 안내
      if (curation.isNew) {
        // /// console.log('새로운 큐레이션이므로 저장 필요');
        alert('멘토를 추가하기 전에 먼저 큐레이션 섹션을 저장해주세요.\n\n1. 제목과 설명을 입력하세요\n2. "큐레이션 저장" 버튼을 클릭하세요\n3. 저장 완료 후 멘토를 추가할 수 있습니다');
        return;
      }

      // 서버에 저장된 큐레이션인 경우 즉시 멘토 추가
      if (typeof setId === 'number') {
        /// console.log('기존 큐레이션에 멘토 추가 (API 호출):', { setId, mentorId: mentor.id });
        
        try {
          const apiResponse = await addMentorToCuration(setId, mentor.id);

          /// console.log('멘토 추가 API 응답:', {apiResponse});

          // API 호출 성공 후 로컬 상태 업데이트
          setCurationSets(prev => prev.map(set => {
            if (set.id !== setId) return set;

            const newMentor = {
              id: mentor.id,
              relation_id: apiResponse.relation_id, // API 응답에서 받은 relation_id 사용
              name: mentor.name,
              department: mentor.department,
              image_url: mentor.image_url,
              tags: mentor.tags || []
            };

            return {
              ...set,
              mentors: [...set.mentors, newMentor],
              isDirty: false // API 호출했으므로 더티 상태 해제
            };
          }));

          setMentorModalOpen(null);
          
          // 섹션 정보 새로고침
          await onRefreshSections();
          alert('멘토가 성공적으로 추가되었습니다.');
          
        } catch (apiError: any) {
          /// console.error('API 에러 상세:', apiError);
          
          // 409 에러 (이미 존재하는 관계)인 경우
          if (apiError.response?.status === 409 || apiError.status === 409 || apiError.message?.includes('이미 존재') || apiError.message?.includes('already exists') || apiError.message?.includes('duplicate')) {
            alert('이 멘토는 이미 해당 큐레이션에 등록되어 있습니다.\n\n페이지를 새로고침하여 최신 상태를 확인해주세요.');
            // 큐레이션 목록 새로고침
            await loadCurations();
            setMentorModalOpen(null);
          } else if (apiError.response?.status === 400 || apiError.status === 400) {
            alert('잘못된 요청입니다. 멘토 정보나 큐레이션 정보를 다시 확인해주세요.');
          } else if (apiError.response?.status === 404 || apiError.status === 404) {
            alert('멘토를 찾을 수 없습니다. 멘토가 삭제되었거나 존재하지 않을 수 있습니다.');
          } else {
            throw apiError; // 다른 에러는 그대로 throw
          }
        }
      }
    } catch (err: any) {
      /// console.error('멘토 추가 실패:', err);
      alert(err.message || '멘토 추가에 실패했습니다.');
    }
  };

  // 멘토를 큐레이션에서 제거
  const removeMentorFromCurationSet = async (setId: string | number, mentorId: number) => {
    try {
      const curation = curationSets.find(c => c.id === setId);
      if (!curation) return;

      // 새로운 큐레이션이거나 서버에 저장되지 않은 상태인 경우 로컬에서만 제거
      if (curation.isNew) {
        /// console.log('새로운 큐레이션에서 멘토 제거 (로컬에서만):', { setId, mentorId });
        
        // 로컬 상태만 업데이트
        setCurationSets(prev => prev.map(set => 
          set.id === setId 
            ? { ...set, mentors: set.mentors.filter(m => m.id !== mentorId), isDirty: true }
            : set
        ));
        return;
      }

        // 서버에 저장된 큐레이션인 경우 API 호출
        if (typeof setId === 'number') {
          /// console.log('기존 큐레이션에서 멘토 제거 (API 호출):', { setId, mentorId });
          
          // personId로 API 호출
          await removeMentorFromCuration(setId, mentorId);

          // API 호출 성공 후 로컬 상태 업데이트
          setCurationSets(prev => prev.map(set => 
            set.id === setId 
              ? { ...set, mentors: set.mentors.filter(m => m.id !== mentorId), isDirty: false }
              : set
          ));

          // 섹션 정보 새로고침
          await onRefreshSections();
          alert('멘토가 성공적으로 제거되었습니다.');
        }
    } catch (err: any) {
      /// console.error('멘토 제거 실패:', err);
      alert(err.message || '멘토 제거에 실패했습니다.');
    }
  };

  // 개별 큐레이션 저장
  const saveSingleCuration = async (setId: string | number) => {
    try {
      const curation = curationSets.find(c => c.id === setId);
      if (!curation) return;

      // /// console.log('커피챗 큐레이션 저장 시작:', { setId, curation });

      if (!curation.title.trim()) {
        alert('큐레이션 제목을 입력해주세요.');
        return;
      }

      if (!curation.description.trim()) {
        alert('큐레이션 설명을 입력해주세요.');
        return;
      }

      setLoading(true);

      if (curation.isNew) {
        // /// console.log('새로운 커피챗 큐레이션 생성 중...');
        // 새로운 큐레이션 생성
        const sectionData = {
          title: curation.title,
          text: curation.description
        };

        const createdSection = await createCoffeeChatCurationSection(sectionData);
        /// console.log('커피챗 큐레이션 생성 완료:', {createdSection});
        
        // 로컬 상태 업데이트
        setCurationSets(prev => prev.map(set => 
          set.id === setId 
            ? { 
                ...set, 
                id: createdSection.id ?? createdSection.coffee_chat_section_id,
                isNew: false,
                isDirty: false
              }
            : set
        ));

        // 섹션 정보 새로고침
        await onRefreshSections();
        alert('커피챗 큐레이션이 성공적으로 저장되었습니다.\n이제 멘토를 추가할 수 있습니다.');
      } else if (typeof setId === 'number' && curation.isDirty) {
        // /// console.log('기존 커피챗 큐레이션 업데이트 중...');
        // 기존 큐레이션 업데이트
        const sectionData = {
          title: curation.title,
          text: curation.description
        };

        await updateCoffeeChatCurationSection(setId, sectionData);
        // /// console.log('커피챗 큐레이션 업데이트 완료');
        
        // 로컬 상태 업데이트
        setCurationSets(prev => prev.map(set => 
          set.id === setId 
            ? { ...set, isDirty: false }
            : set
        ));

        // 섹션 정보 새로고침
        await onRefreshSections();
        alert('커피챗 큐레이션이 성공적으로 업데이트되었습니다.');
      } else {
        alert('저장할 변경사항이 없습니다.');
      }
    } catch (err: any) {
      /// console.error('커피챗 큐레이션 저장 실패:', err);
      alert(err.message || '커피챗 큐레이션 저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 모든 큐레이션 저장
  const saveAllCurations = async () => {
    try {
      setLoading(true);
      setError(null);

      let savedCount = 0;

      for (const curation of curationSets) {
        // 새 큐레이션이면서 내용이 있는 경우에만 생성 (멘토가 없는 빈 큐레이션만)
        if (curation.isNew && (curation.title || curation.description) && curation.mentors.length === 0) {
          const sectionData = {
            title: curation.title || '새 커피챗 큐레이션',
            text: curation.description || '새 커피챗 큐레이션 설명'
          };

          await createCoffeeChatCurationSection(sectionData);
          savedCount++;
        }
        // 기존 큐레이션이면서 수정된 경우 업데이트
        else if (!curation.isNew && curation.isDirty && typeof curation.id === 'number') {
          const sectionData = {
            title: curation.title,
            text: curation.description
          };

          await updateCoffeeChatCurationSection(curation.id, sectionData);
          savedCount++;
        }
      }

      // 저장 후 목록 새로고침
      await loadCurations();
      
      // 섹션 정보도 새로고침
      await onRefreshSections();
      
      if (savedCount > 0) {
        alert(`${savedCount}개의 커피챗 큐레이션이 성공적으로 저장되었습니다.`);
      } else {
        alert('저장할 변경사항이 없습니다.\n\n참고: 멘토가 추가된 새 큐레이션은 이미 자동으로 저장되었습니다.');
      }
    } catch (err: any) {
      /// console.error('커피챗 큐레이션 저장 실패:', err);
      setError(err.message || '커피챗 큐레이션 저장에 실패했습니다.');
      alert(err.message || '커피챗 큐레이션 저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>커피챗 큐레이션</SectionTitle>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </SectionHeader>

      {loading && <LoadingMessage>로딩 중...</LoadingMessage>}

      {curationSets.map((curationSet, setIndex) => (
        <CurationContent key={curationSet.id} style={{ marginBottom: '32px' }}>
          <CurationForm>
            <FormGroup>
              <SectionTitle as="label" style={{ fontSize: '12px', color: 'rgba(46, 47, 51, 0.6)' }}>
                커피챗 큐레이션 {setIndex + 1}
              </SectionTitle>
              <InputField
                placeholder="제목을 입력해주세요."
                value={curationSet.title}
                onChange={(value) => updateCurationSet(curationSet.id, 'title', value)}
              />
            </FormGroup>
            <InputField
              placeholder="설명을 입력해주세요."
              className="descriptionInput"
              value={curationSet.description}
              onChange={(value) => updateCurationSet(curationSet.id, 'description', value)}
            />
            <CurationButtons>
              {/* 개별 큐레이션 저장 버튼 */}
              <Button
                variant={curationSet.isNew || curationSet.isDirty ? "primary" : "outlined"}
                onClick={() => saveSingleCuration(curationSet.id)}
                disabled={loading}
              >
                {loading ? '저장 중...' :
                 curationSet.isNew ? '큐레이션 저장' :
                 curationSet.isDirty ? '변경사항 저장' : '저장됨'}
              </Button>
              <Button
                variant="outlined"
                onClick={addCurationSet}
              >
                <PlusIcon />
              </Button>
              <Button
                variant="outlined"
                onClick={() => removeCurationSet(curationSet.id)}
                disabled={curationSets.length <= 1}
              >
                <MinusIcon />
              </Button>
            </CurationButtons>
          </CurationForm>

          {/* 멘토 목록 표시 */}
          <LectureGrid>
            {curationSet.mentors.map((mentor) => (
              <LectureCard key={mentor.id}>
                <LectureCardImage>
                  {mentor.image_url ? (
                    <img src={mentor.image_url} alt={mentor.name} />
                  ) : (
                    <NoImage>이미지 없음</NoImage>
                  )}
                </LectureCardImage>
                <LectureCardContent>
                  <LectureTitle>{mentor.name}</LectureTitle>
                  <LecturerName>{mentor.department}</LecturerName>
                  {mentor.tags && mentor.tags.length > 0 && (
                    <LectureTags>
                      {mentor.tags.map((tag, idx) => (
                        <Tag key={idx}>{tag.trim()}</Tag>
                      ))}
                    </LectureTags>
                  )}
                </LectureCardContent>
                <RemoveLectureButton
                  onClick={() => removeMentorFromCurationSet(curationSet.id, mentor.id)}
                >
                  <MinusIcon />
                </RemoveLectureButton>
              </LectureCard>
            ))}

            {/* 멘토 추가 버튼 */}
            <AddLectureCard
              $disabled={curationSet.isNew}
              onClick={() => {
                if (curationSet.isNew) {
                  alert('멘토를 추가하기 전에 먼저 큐레이션을 저장해주세요.');
                  return;
                }
                setMentorModalOpen(String(curationSet.id));
              }}
              title={curationSet.isNew ? '큐레이션을 먼저 저장해주세요' : '멘토 추가'}
            >
              <PlusIcon />
              <span>멘토 추가</span>
              {curationSet.isNew && (
                <small style={{ fontSize: '10px', color: '#999', marginTop: '4px' }}>
                  (큐레이션 저장 후 가능)
                </small>
              )}
            </AddLectureCard>
          </LectureGrid>

          {/* 멘토 선택 모달 */}
          {mentorModalOpen === String(curationSet.id) && (
            <LectureModal>
              <ModalOverlay onClick={() => setMentorModalOpen(null)} />
              <ModalContent>
                <ModalHeader>
                  <h3>멘토 선택</h3>
                  <button onClick={() => setMentorModalOpen(null)}>×</button>
                </ModalHeader>
                <ModalBody>
                  <LectureList>
                    {availableMentors.map((mentor) => {
                      const isAlreadyAdded = curationSet.mentors.some(m => m.id === mentor.id);

                      return (
                        <LectureListItemRow
                          key={mentor.id}
                          $alreadyAdded={isAlreadyAdded}
                          onClick={() => {
                            if (isAlreadyAdded) {
                              alert('이 멘토는 이미 추가되어 있습니다.');
                              return;
                            }
                            addMentorToCurationSet(curationSet.id, mentor);
                          }}
                          title={isAlreadyAdded ? '이미 추가된 멘토' : '클릭하여 추가'}
                        >
                          <LectureListImage>
                            {mentor.image_url ? (
                              <img src={mentor.image_url} alt={mentor.name} />
                            ) : (
                              <NoImage>이미지 없음</NoImage>
                            )}
                          </LectureListImage>
                          <LectureListContent>
                            <h4>{mentor.name}</h4>
                            <p>{mentor.department}</p>
                            {mentor.tags && mentor.tags.length > 0 && (
                              <LectureTags>
                                {mentor.tags.map((tag, idx) => (
                                  <Tag key={idx}>{tag.trim()}</Tag>
                                ))}
                              </LectureTags>
                            )}
                          </LectureListContent>
                          {isAlreadyAdded && (
                            <AddedIndicator>
                              ✓ 추가됨
                            </AddedIndicator>
                          )}
                        </LectureListItemRow>
                      );
                    })}
                  </LectureList>
                </ModalBody>
              </ModalContent>
            </LectureModal>
          )}
        </CurationContent>
      ))}

      {/* 큐레이션 저장 버튼 */}
      <SectionActions>
        <Button
          variant="primary"
          onClick={saveAllCurations}
          disabled={loading}
        >
          {loading ? '저장 중...' : '커피챗 큐레이션 저장'}
        </Button>
      </SectionActions>
    </Section>
  );
};

// 팝업 배너 섹션
const PopupBannerSection: React.FC = () => {
  const [popup, setPopup] = useState<PopupBannerType | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getActivePopups()
      .then(([first]) => {
        if (first) {
          setPopup(first);
          setPreviewUrl(first.image_url);
          setLinkUrl(first.link_url ?? '');
        }
      })
      .catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!imageFile && !popup) {
      alert('이미지를 업로드해주세요.');
      return;
    }
    try {
      setLoading(true);
      if (imageFile) {
        const result = popup
          ? await updatePopupBanner(popup.id, imageFile, linkUrl)
          : await createPopupBanner(imageFile, linkUrl);
        setPopup(result);
        setPreviewUrl(result.image_url);
        setImageFile(null);
      } else if (popup) {
        const result = await updatePopupBanner(popup.id, null, linkUrl);
        setPopup(result);
      }
      alert('팝업 배너가 저장되었습니다.');
    } catch (err: any) {
      alert(err?.message || '저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    e.target.value = '';
  };

  const handleDelete = async () => {
    if (!popup) return;
    if (!window.confirm('팝업 배너를 삭제하시겠습니까?')) return;
    try {
      setLoading(true);
      await deletePopupBanner(popup.id);
      setPopup(null);
      setPreviewUrl(null);
      setLinkUrl('');
      setImageFile(null);
      alert('팝업 배너가 삭제되었습니다.');
    } catch (err: any) {
      alert(err.message || '삭제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>팝업 배너</SectionTitle>
      </SectionHeader>

      {previewUrl && (
        <PopupPreview src={previewUrl} alt="팝업 배너 미리보기" />
      )}

      <PopupImageRow>
        <PopupHint>
          가로 720px, 세로 1022px (비율 1:1.42) 의 JPG, PNG 파일 업로드
        </PopupHint>
        <PopupUploadButton
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={loading}
        >
          이미지 업로드
        </PopupUploadButton>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </PopupImageRow>

      <PopupTitleRow>
        <PopupUrlInput
          type="text"
          placeholder="배너 클릭 시 연결될 URL을 입력해주세요."
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
        />
        <PopupDeleteButton
          type="button"
          onClick={handleDelete}
          disabled={loading || !popup}
        >
          팝업 배너 삭제
        </PopupDeleteButton>
      </PopupTitleRow>

      <PopupSaveRow>
        <Button variant="primary" onClick={handleSave} disabled={loading}>
          {loading ? '저장 중...' : '저장하기'}
        </Button>
      </PopupSaveRow>
    </Section>
  );
};

// 저장 버튼 섹션
const SaveActions: React.FC = () => (
  <SaveSection>
    <SaveActionsWrapper>
      <TempSaveButton>
        <Button
          variant="text"
          onClick={() => console.log('Temp save clicked')}
        >
          임시 저장
        </Button>
      </TempSaveButton>
    </SaveActionsWrapper>
  </SaveSection>
);

const HomeManagementComponent: React.FC = () => {
  // 커피챗 섹션 정보 새로고침 함수
  const loadCoffeeChatUIData = React.useCallback(async () => {
    try {
      const uiResponse = await getAdminCoffeeChatUI();
      return {
        banners: uiResponse.data.banners || [],
        sections: uiResponse.data.sections || []
      };
    } catch (err: any) {
      /// console.error('커피챗 UI 데이터 조회 실패:', err);
      return {
        banners: [],
        sections: []
      };
    }
  }, []);

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>홈 관리</PageTitle>
      </PageHeader>

      <PageContent>
        <BannerSection />
        <PopupBannerSection />
        <CategorySectionComponent />
        <ClassCurationManagement />
        <CoffeeChatCurationManagement onRefreshSections={loadCoffeeChatUIData} />
        <SaveActions />
      </PageContent>
    </PageContainer>
  );
};

export default HomeManagementComponent;
